import type { ReactNode } from "react";
import { AlertCircle, Database, Plug, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/Button/Button";
import { errorMessage } from "../format";
import type { Source, SourceState } from "../types";
import { sourceLabel } from "../types";
import styles from "../Analytics.module.css";
export function Loading({ label = "Loading analytics…" }: { label?: string }) {
  return (
    <div className={styles.loading} role="status">
      <RefreshCw size={20} className={styles.spin} />
      <span>{label}</span>
    </div>
  );
}
export function ErrorState({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  return (
    <div className={styles.notice} role="alert">
      <AlertCircle size={20} />
      <div>
        <strong>Unable to load this view</strong>
        <p>{errorMessage(error)}</p>
        {retry && (
          <Button variant="secondary" onClick={retry}>
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}
export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <Database size={28} />
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}
export function SourceBadge({
  source,
  state,
  connected = false,
}: {
  source: Source;
  state?: SourceState;
  connected?: boolean;
}) {
  const status = !state
    ? connected
      ? "Status not checked"
      : "Not linked"
    : state.connection_status === "not_connected"
      ? "Not linked"
      : state.connection_status === "reauth_required"
        ? "Reconnect needed"
        : state.connection_status === "permission_denied"
          ? "Access needed"
          : state.sync_status === "running" || state.sync_status === "queued"
            ? "Refreshing"
            : state.sync_status === "failed"
              ? "Refresh failed"
              : state.freshness === "stale"
                ? "Refresh recommended"
                : state.data_status === "partial"
                  ? "Connected · Partial history"
                  : state.data_status === "empty"
                    ? "No activity"
                    : state.data_status === "unavailable"
                      ? "No report yet"
                      : state.freshness === "unknown"
                        ? "Freshness unknown"
                        : "Connected · Up to date";
  return (
    <span className={styles.badge}>
      <span
        className={styles.dot}
        data-state={
          status === "Connected · Up to date"
            ? "good"
            : status === "Not linked"
              ? "off"
              : "attention"
        }
      />
      {source === "ga4" ? "GA4" : "GSC"} · {status}
    </span>
  );
}
export function MissingSource({
  source,
  href,
}: {
  source: Source;
  href: string;
}) {
  return (
    <div className={styles.notice}>
      <Plug size={22} />
      <div>
        <strong>Link {sourceLabel[source]} to this website</strong>
        <p>
          {source === "gsc"
            ? "Choose the Search Console site to show how people find this website in Google Search."
            : "Google Analytics has not been linked to this website yet. Choose its GA4 property to show visits, active users and page views."}
        </p>
        <Link
          className={styles.textLink}
          to={`${href}${href.includes("?") ? "&" : "?"}connect=${source}`}
        >
          Choose {source === "ga4" ? "GA4 property" : "Search Console site"} →
        </Link>
      </div>
    </div>
  );
}
export function SourceNotice({
  source,
  state,
}: {
  source: Source;
  state: SourceState;
}) {
  if (state.connection_status === "not_connected") return null;
  const message =
    state.connection_status === "reauth_required"
      ? "Reconnect this account to retrieve new data. Saved data may still be available."
      : state.connection_status === "permission_denied"
        ? "This account no longer has permission to read the selected resource."
        : state.sync_status === "failed"
          ? "The last refresh failed. Previous successful results remain available."
          : state.data_status === "partial"
            ? "Some dates or report details are missing. Comparisons may be unavailable."
            : state.freshness === "stale"
              ? "Saved results are available. Refresh to check for newer data."
              : state.data_status === "unavailable"
                ? "No successful report is available for this period. Refresh data or check the connection."
                : null;
  return message ? (
    <div className={styles.subtleNotice}>
      <AlertCircle size={16} />
      <span>
        <strong>{sourceLabel[source]}:</strong> {message}
      </span>
    </div>
  ) : null;
}
